import type { ShadowProfile } from "@sitescene/types";

import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import { queryKeys } from "@/api/query-keys";
import { retryUnlessPermanent } from "@/api/retry";
import { useAuth } from "@/providers/auth-provider";

export interface ShadowProfileValue {
	error: Error | null;
	/** Whether the Global section is open to this user. False until loaded. */
	globalAccess: boolean;
	isLoading: boolean;
}

/**
 * What the signed-in user may do beyond their organization list.
 *
 * Client-side this only decides what to render: the Global section link and
 * its pages. Every global route re-asserts the same `ShadowAccess` flag on the
 * server, so a viewer who forces the URL gets 403s, not data.
 */
export const useShadowProfile = (): ShadowProfileValue => {
	const { isAuthenticated, isLoadingAuth } = useAuth();

	// Same guard as the organization list: without it the query fires before a
	// token exists and its 401 bounces the visitor through logout.
	const isReady = isAuthenticated && !isLoadingAuth;

	const { data, error, isLoading } = useQuery({
		enabled: isReady,
		queryFn: async () => {
			const response = await apiClient.get<ShadowProfile>(ENDPOINTS.shadow.me);
			return response.data;
		},
		queryKey: queryKeys.me,
		retry: retryUnlessPermanent,
	});

	return {
		error: error instanceof Error ? error : null,
		globalAccess: data?.globalAccess === true,
		isLoading: !isReady || isLoading,
	};
};
